/**
 * Phase 32 Plan 32-3 — value-tag card list (UI-SPEC §247-254).
 *
 * Renders the tags applied to a single engineer (drill-down page). Per card:
 *   - Avatar chip (initials in `h-8 w-8 rounded-full`).
 *   - Tagger name + relative timestamp ("Jiří Fabián · 3 days ago").
 *   - Project name + category badge (gray-neutral chip per UI-SPEC §251).
 *   - Date span "Mar 4 – Mar 18, 2026" via date-fns.
 *   - Note preview (first 200 chars; click to expand).
 *   - Edit / Delete icon buttons visible only when
 *     `tag.taggedById === currentUser.id || isAdmin`.
 *
 * Empty state per UI-SPEC §160 — message + (parent's) CTA.
 *
 * Delete confirmation uses ConfirmModal variant="danger" with UI-SPEC §171
 * copy verbatim:
 *   title:       "Delete value tag?"
 *   message:     "This tag will be hidden from reports. The original audit
 *                 record is preserved."
 *   confirmText: "Delete"
 */
import { useMemo, useState, type ReactElement } from 'react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../common/Spinner';
import { ConfirmModal } from '../common/Modal';
import ValueTagFormModal from './ValueTagFormModal';
import {
  useValueTagsForUser,
  useDeleteValueTag,
} from '../../hooks/useValueTags';
import { useAuth } from '../../services/authContext';
import type { ValueTag, ValueTagCategory } from '../../services/api/valueTags';

const CATEGORY_LABELS: Record<ValueTagCategory, string> = {
  feature: 'Feature',
  bugfix: 'Bug fix',
  refactor: 'Refactor',
  infra: 'Infrastructure',
  docs: 'Documentation',
  customer_request: 'Customer request',
  tech_debt: 'Tech debt',
  other: 'Other',
};

function initialsOf(p: { firstName: string | null; lastName: string | null; email: string }): string {
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  const a = first.charAt(0);
  const b = last.charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  return p.email.charAt(0).toUpperCase();
}

function fullName(p: { firstName: string | null; lastName: string | null; email: string }): string {
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  if (first || last) return `${first} ${last}`.trim();
  return p.email;
}

function formatDateSpan(fromYmd: string, toYmd: string): string {
  // YYYY-MM-DD → render "MMM d – MMM d, yyyy" if same year, else
  // "MMM d, yyyy – MMM d, yyyy". parseISO is timezone-stable for date-only.
  try {
    const from = parseISO(fromYmd);
    const to = parseISO(toYmd);
    const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
    if (sameYear) {
      return `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`;
    }
    return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
  } catch {
    return `${fromYmd} – ${toYmd}`;
  }
}

const NOTE_PREVIEW_LIMIT = 200;

interface ValueTagCardProps {
  tag: ValueTag;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function ValueTagCard({ tag, canEdit, onEdit, onDelete }: ValueTagCardProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const note = tag.note ?? '';
  const isLong = note.length > NOTE_PREVIEW_LIMIT;
  const displayNote = expanded || !isLong ? note : `${note.slice(0, NOTE_PREVIEW_LIMIT)}…`;
  const tagger = tag.taggedBy;
  const relativeTs = (() => {
    try {
      return formatDistanceToNow(parseISO(tag.createdAt), { addSuffix: true });
    } catch {
      return tag.createdAt;
    }
  })();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
          {initialsOf(tagger)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="font-semibold text-gray-900">{fullName(tagger)}</span>
            <span className="text-gray-500">· {relativeTs}</span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span className="font-medium">{tag.project.name ?? '(no project name)'}</span>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {CATEGORY_LABELS[tag.category]}
            </span>
            <span className="text-gray-500">{formatDateSpan(tag.fromDate, tag.toDate)}</span>
          </div>

          {note && (
            <p className="mt-2 whitespace-pre-line text-sm text-gray-700">
              {displayNote}
              {isLong && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="ml-1 text-primary-600 hover:underline"
                >
                  {expanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </p>
          )}
        </div>

        {canEdit && (
          <div className="flex flex-none items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit value tag"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              aria-label="Delete value tag"
              className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export interface ValueTagListProps {
  userId: number;
}

export function ValueTagList({ userId }: ValueTagListProps): ReactElement {
  const { user, isAdmin } = useAuth();
  const tagsQ = useValueTagsForUser(userId);
  const deleteMut = useDeleteValueTag(userId);

  const [editingTag, setEditingTag] = useState<ValueTag | null>(null);
  const [confirmDeleteTag, setConfirmDeleteTag] = useState<ValueTag | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const tags = useMemo(() => tagsQ.data ?? [], [tagsQ.data]);

  if (tagsQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner size="sm" />
      </div>
    );
  }

  if (tagsQ.error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        Could not load value tags: {(tagsQ.error as Error).message}
      </div>
    );
  }

  if (tags.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <p className="text-sm text-gray-600">
          No value tags yet. Recognise this person&apos;s work that doesn&apos;t show
          up in commit counts.
        </p>
      </div>
    );
  }

  const canEditTag = (tag: ValueTag): boolean => {
    if (!user) return false;
    if (isAdmin) return true;
    return tag.taggedById === user.id;
  };

  return (
    <div className="space-y-3">
      {tags.map((tag) => (
        <ValueTagCard
          key={tag.id}
          tag={tag}
          canEdit={canEditTag(tag)}
          onEdit={() => setEditingTag(tag)}
          onDelete={() => {
            setDeleteError(null);
            setConfirmDeleteTag(tag);
          }}
        />
      ))}

      {editingTag && (
        <ValueTagFormModal
          isOpen
          onClose={() => setEditingTag(null)}
          tag={editingTag}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDeleteTag}
        onClose={() => setConfirmDeleteTag(null)}
        title="Delete value tag?"
        message="This tag will be hidden from reports. The original audit record is preserved."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMut.isPending}
        error={deleteError ?? undefined}
        onConfirm={async () => {
          if (!confirmDeleteTag) return;
          try {
            setDeleteError(null);
            await deleteMut.mutateAsync(confirmDeleteTag.id);
            setConfirmDeleteTag(null);
          } catch (err) {
            setDeleteError(
              err instanceof Error
                ? err.message
                : 'Could not delete the value tag. Please try again.',
            );
          }
        }}
      />
    </div>
  );
}

export default ValueTagList;
